import { useKeyboard } from '@react-native-community/hooks';
import { useIsFocused } from '@react-navigation/native';
import { presentLoginSheet } from 'app/atoms';
import { AppleLoginButton } from 'app/components/authentication/AppleLoginButton';
import {
  AuthenticationMethodOption,
  LastUsedAuthenticationMethodBadge,
} from 'app/components/authentication/AuthenticationMethodOption';
import { FacebookLoginButton } from 'app/components/authentication/FacebookLoginButton';
import { GoogleLoginButton } from 'app/components/authentication/GoogleLoginButton';
import { DownloadAppSection } from 'app/components/authentication/DownloadAppSection';
import { LoginAsPromptModal } from 'app/components/use-login-as-prompt';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { Input } from 'app/design/primitives/input';
import { Modal } from 'app/design/primitives/modal';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import TextDivider from 'app/design/primitives/text-divider';

import { useForm } from 'app/hooks/form';
import { useNavigation } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { useLastAuthenticationMethod } from 'app/lib/authentication-method';
import { env } from 'app/lib/env';
import 'app/lib/form'; // this has the password string setup
import { prepareLoginAsEmail } from 'app/lib/login-as-email';
import { type CreateTokenDTO, useCreateIdentity } from 'app/network/identity';
import {
  SignUpSource,
  useAskForNotificationPermission,
  useSocialConnect,
  useNavigateToSignUpForm,
} from 'app/screens/onboarding/signup';
import { AmplifyAuthConfiguration } from 'app/services/amplify';
import { Amplify } from 'aws-amplify';
import { useAtom } from 'jotai/index';
import React, { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TextInput } from 'react-native';
import { Platform, ScrollView, View } from 'react-native';
import * as yup from 'yup';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useIsGuestWeb, useUISize } from 'app/utils/web-helpers';
Amplify.configure({ Auth: { Cognito: AmplifyAuthConfiguration } });

const LoginSheet = function LoginSheet({
  state,
  onLoginComplete,
}: {
  state: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  onLoginComplete?: () => void;
}) {
  const { t } = useTranslation('login');
  const { t: tAPI } = useTranslation('api-errors');
  const theme = useTheme();
  const navigation = useNavigation();
  const navigateToSignUpForm = useNavigateToSignUpForm(false, undefined, SignUpSource.Billboard);
  const isFocused = useIsFocused();
  const isGuestWeb = useIsGuestWeb();
  const { isLargeUI, isSmallUI } = useUISize();
  const isGuestMobileWeb = isGuestWeb && isSmallUI;
  const isGuestDesktopWeb = isGuestWeb && isLargeUI;

  const keyboard = useKeyboard();
  const { bottom: bottomSafeInset } = useSafeAreaInsets();

  const [isLoginSheetVisible, setIsLoginSheetVisible] = state;
  const lastUsedMethod = useLastAuthenticationMethod(isLoginSheetVisible);

  const askForNotificationPermission = useAskForNotificationPermission();

  const { handleSocialIdP, loginButtonsState, didSSOLogin, ssoError, goBackWhenIsLoggedIn, setGoBackWhenIsLoggedIn } =
    useSocialConnect(() => {
      setIsLoginSheetVisible(false);
      onLoginComplete?.();
    });

  const schema: yup.ObjectSchema<CreateTokenDTO> = yup.object({
    email: yup
      .string()
      .required()
      .test({
        name: 'customEmailValidation',
        message: t('fieldErrorDefault', { ns: 'common' }),
        test: (value) =>
          // 'email1 -> email2' value is for login-as; see handleSubmit() below
          value.includes(' -> ') || yup.string().email().isValidSync(value),
      }),
    password: yup.string().min(8).required(),
  });
  const { control, formState, handleSubmit } = useForm({
    schema,
    validationMode: 'onBlur',
    defaultValues:
      env.APP_ENV === 'local'
        ? { email: env.DEFAULT_USERNAME ?? '', password: env.DEFAULT_PASSWORD ?? '' }
        : { email: '', password: '' },
  });

  const createIdentity = useCreateIdentity({
    onSuccess: async () => {
      track(Event.LoginSuccess, { provider: 'Email' });
      await askForNotificationPermission();
      // Wait until isLoggedIn atom is set, then go back to the Babillard
      setGoBackWhenIsLoggedIn(true);
    },
  });

  const onSubmit = handleSubmit(async (form: CreateTokenDTO) => {
    form.email = await prepareLoginAsEmail(form.email);
    createIdentity.mutate(form);
  });

  const refPassword = useRef<TextInput>(null);

  const [ssoSize, setSsoSize] = useState<number>(0);
  const refSSO = useRef<View>(null);
  const onLayoutSSO = () => {
    if (!refSSO.current) return;
    if (ssoSize) return;
    refSSO.current.measure((_x, _y, _width, height, _pageX, _pageY) => {
      if (height) {
        setSsoSize(height + 2 * theme.spacing['spacing.general.x4']);
      }
    });
  };

  const onForgotPassword = () => {
    setIsLoginSheetVisible(false);
    // Wait until hide animation completes to navigate to new screen
    setTimeout(() => {
      navigation.navigate('password-recovery', undefined, { pop: true });
    }, 300);
  };

  const isSSOEnabled = isFeatureEnabled(FeatureFlag.sso);
  const socialLoginButtons = isSSOEnabled ? (
    <>
      <Row
        gap={isGuestDesktopWeb ? undefined : 'spacing.general.x2'}
        style={isGuestDesktopWeb ? { gap: 10 } : undefined}
      >
        <AuthenticationMethodOption flex lastUsedMethod={lastUsedMethod} method="Facebook">
          <FacebookLoginButton handleSocialIdP={handleSocialIdP} iconOnly loginButtonsState={loginButtonsState} />
        </AuthenticationMethodOption>

        <AuthenticationMethodOption flex lastUsedMethod={lastUsedMethod} method="Google">
          <GoogleLoginButton handleSocialIdP={handleSocialIdP} iconOnly loginButtonsState={loginButtonsState} />
        </AuthenticationMethodOption>

        {Platform.OS !== 'android' ? (
          <AuthenticationMethodOption flex lastUsedMethod={lastUsedMethod} method="Apple">
            <AppleLoginButton handleSocialIdP={handleSocialIdP} iconOnly loginButtonsState={loginButtonsState} />
          </AuthenticationMethodOption>
        ) : null}
      </Row>

      {ssoError ? <RemoteError {...{ error: ssoError, message: tAPI(ssoError.message) }} /> : null}
    </>
  ) : null;

  const loginContent = (
    <>
      <Column
        gap="spacing.general.x4"
        paddingHorizontal={isGuestDesktopWeb ? 'spacing.general.x10' : 'spacing.general.x6'}
        style={{
          alignSelf: 'center',
          maxWidth: 480,
          width: '100%',
          paddingBottom: isGuestDesktopWeb
            ? 64
            : Platform.OS !== 'web'
              ? bottomSafeInset + theme.spacing['spacing.general.x4']
              : undefined,
          marginBottom:
            keyboard.keyboardShown && isSSOEnabled && (Platform.OS === 'android' || Platform.OS === 'ios')
              ? -ssoSize +
                (Platform.OS === 'android' ? theme.spacing['spacing.general.x8'] : theme.spacing['spacing.general.x1'])
              : 0,
        }}
      >
        {!isGuestMobileWeb ? (
          <Button
            onPress={() => setIsLoginSheetVisible(false)}
            pressableType={PressableType.GestureHandler}
            style={
              isGuestDesktopWeb
                ? { position: 'absolute', right: 24, top: 24, height: 40, width: 40 }
                : { position: 'absolute', top: 4, left: 16 }
            }
            variant={isGuestDesktopWeb ? 'plain' : undefined}
          >
            <IconX size={24} strokeWidth={ICON_STROKE_WIDTH} />
          </Button>
        ) : null}

        <Text
          fontWeight={isGuestDesktopWeb ? '600' : '700'}
          marginBottom={isGuestDesktopWeb ? 'spacing.general.x4' : 'spacing.general.x2'}
          style={{
            marginHorizontal: isGuestDesktopWeb ? 0 : 16 + 30,
            color: isGuestDesktopWeb ? theme.colors['text.primary'] : undefined,
          }}
          textAlign="center"
          variant={isGuestDesktopWeb ? 'headerMedium' : 'headerXSmall'}
        >
          {t('title')}
        </Text>

        <Controller
          {...{ control }}
          name="email"
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect={false}
              error={fieldState.error}
              label={t('emailFieldLabel')}
              labelRight={lastUsedMethod === 'Email' ? <LastUsedAuthenticationMethodBadge method="Email" /> : null}
              labelFontWeight={isGuestDesktopWeb ? '400' : undefined}
              fieldHeight={isGuestDesktopWeb ? 48 : undefined}
              fieldBorderRadius={isGuestDesktopWeb ? 'radius.component.fields.input' : undefined}
              fieldBorderWidth={isGuestDesktopWeb ? 1.5 : undefined}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() => refPassword.current?.focus()}
              placeholder={t('emailFieldPlaceholder')}
              returnKeyType="next"
              submitBehavior="submit"
              testID="login-email-input"
              textContentType="emailAddress"
              value={field.value}
            />
          )}
        />

        <Controller
          {...{ control }}
          name="password"
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="password"
              autoCorrect={false}
              error={fieldState.error}
              innerRef={refPassword}
              label={t('passwordFieldLabel')}
              labelFontWeight={isGuestDesktopWeb ? '400' : undefined}
              fieldHeight={isGuestDesktopWeb ? 48 : undefined}
              fieldBorderRadius={isGuestDesktopWeb ? 'radius.component.fields.input' : undefined}
              fieldBorderWidth={isGuestDesktopWeb ? 1.5 : undefined}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() => formState.isValid && void onSubmit()}
              placeholder={t('passwordFieldPlaceholder')}
              returnKeyType="go"
              secureTextEntry
              testID="login-password-input"
              textContentType="password"
              value={field.value}
            />
          )}
        />

        {createIdentity.error ? (
          <RemoteError {...{ error: createIdentity.error, message: tAPI(createIdentity.error.message) }} />
        ) : null}

        <Button
          onPress={onForgotPassword}
          pressableType={PressableType.GestureHandler}
          variant={isGuestDesktopWeb ? 'plain' : undefined}
        >
          <Text
            flex={1}
            fontSize={isGuestDesktopWeb ? 14 : undefined}
            fontWeight="700"
            textAlign="center"
            textDecorationLine="underline"
            style={isGuestDesktopWeb ? { color: theme.colors['text.primary'] } : undefined}
          >
            {t('forgotPassword')}
          </Text>
        </Button>

        <Button
          loading={createIdentity.isPending || (goBackWhenIsLoggedIn && !didSSOLogin)}
          marginBottom={Platform.OS !== 'web' && !isSSOEnabled ? 'spacing.general.x4' : undefined}
          pressableType={PressableType.GestureHandler}
          onPress={() => void onSubmit()}
          style={
            isGuestDesktopWeb
              ? {
                  height: theme.spacing['spacing.general.x14'],
                  borderRadius: theme.borderRadii['radius.full'],
                  backgroundColor: theme.colors['component.button.bg.primary'],
                }
              : undefined
          }
          testID="login-submit-button"
          variant={isGuestDesktopWeb ? 'primary' : 'signup'}
        >
          {isGuestDesktopWeb ? (
            <Text color="white" fontSize={16} fontWeight="600">
              {t('guestPrimaryButtonText')}
            </Text>
          ) : (
            t('primaryButtonText')
          )}
        </Button>

        {isSSOEnabled ? (
          <View collapsable={false} ref={refSSO}>
            <Column
              gap="spacing.general.x4"
              marginTop={isGuestDesktopWeb ? undefined : 'spacing.general.x4'}
              onLayout={onLayoutSSO}
              paddingBottom={Platform.OS !== 'web' ? 'spacing.general.x4' : undefined}
            >
              {isGuestDesktopWeb ? (
                <Row alignItems="center" gap="spacing.general.x3">
                  <View style={{ backgroundColor: theme.colors['border.divider'], flex: 1, height: 1 }} />
                  <Text color="text.primary" variant="bodySmall">
                    {t('or', { ns: 'common' })}
                  </Text>
                  <View style={{ backgroundColor: theme.colors['border.divider'], flex: 1, height: 1 }} />
                </Row>
              ) : (
                <Column marginVertical="spacing.general.x1">
                  <TextDivider text={t('or', { ns: 'common' })} />
                </Column>
              )}
              {socialLoginButtons}
            </Column>
          </View>
        ) : null}

        <Row
          alignItems="center"
          justifyContent="center"
          gap="spacing.general.x2"
          paddingBottom="spacing.general.x4"
          paddingTop={isGuestDesktopWeb ? 'spacing.general.x2' : undefined}
        >
          <Text
            color="text.secondary"
            fontSize={14}
            fontWeight={isGuestDesktopWeb ? '400' : undefined}
            style={isGuestDesktopWeb ? { color: theme.colors['text.secondary'] } : undefined}
          >
            {t('noAccountYetPrefix')}
          </Text>
          <Button onPress={() => navigateToSignUpForm()} variant="plain">
            <Text
              color="text.interactive"
              fontSize={isGuestDesktopWeb ? 16 : 14}
              fontWeight="600"
              textDecorationLine="underline"
            >
              {t('noAccountYet')}
            </Text>
          </Button>
        </Row>
      </Column>
      {Platform.OS === 'web' ? <DownloadAppSection /> : null}
      <LoginAsPromptModal />
    </>
  );

  if (isGuestDesktopWeb) {
    return (
      <Modal animationType="fade" onClose={() => setIsLoginSheetVisible(false)} open={isLoginSheetVisible} transparent>
        <Column
          backgroundColor="surface.default"
          borderRadius="radius.lg"
          overflow="hidden"
          style={{ maxHeight: '90%', maxWidth: 480, width: 'calc(100% - 32px)' as unknown as number }}
        >
          <ScrollView
            bounces={false}
            contentContainerStyle={{ flexGrow: 1 }}
            keyboardShouldPersistTaps="handled"
            style={{ flexShrink: 1 }}
          >
            {loginContent}
          </ScrollView>
        </Column>
      </Modal>
    );
  }

  return (
    <BottomSheet
      onClose={() => setIsLoginSheetVisible(false)}
      open={isLoginSheetVisible ? isFocused : false}
      showIndicator={isGuestMobileWeb}
      containerStyle={
        Platform.OS === 'web'
          ? {
              borderRadius: 24,
              overflow: 'hidden',
            }
          : undefined
      }
    >
      {loginContent}
    </BottomSheet>
  );
};

export const useLoginSheet = (onComplete?: () => void) => {
  const loginSheetState = useAtom(presentLoginSheet);
  const [isLoginSheetVisible, setIsLoginSheetVisible] = loginSheetState;

  const sheetWasShown = useRef(false);

  const showLoginSheet = () => {
    setIsLoginSheetVisible(true);
    sheetWasShown.current = true;
  };

  useEffect(() => {
    if (sheetWasShown.current && !isLoginSheetVisible) {
      sheetWasShown.current = false;
      const completionTimer = setTimeout(() => onComplete?.(), 100);
      return () => clearTimeout(completionTimer);
    }
  }, [isLoginSheetVisible, onComplete]);

  return { showLoginSheet, loginSheetState };
};

export { LoginSheet };
